import { useState, useEffect, useRef } from 'react';
import useAsyncState from 'hooks/useAsyncState.hook';
import CAMPAIGNS from 'consts/campaigns.consts';
import NeoCard from 'design/design_components/neo/panel/NeoCard.base';
import NeoTable from 'design/design_components/neo/table/NeoTable.base';
import NeoTableColumn from 'design/design_components/neo/table/NeoTableColumn.base';
import NeoInputTextFilter from 'design/design_components/neo/table/NeoInputTextFilter.base';
import NeoDateRangeFilter from 'design/design_components/neo/table/NeoDateRangeFilter.base';
import NeoMultiSelectFilter from 'design/design_components/neo/table/NeoMultiSelectFilter.base';
import NeoCellDataStatus from 'design/design_components/neo/data/NeoCellDataStatus.base';
import InternalSpinner from 'components/InternalSpinner.component';
import DataFetchError from 'components/DataFetchError.component';
import CampaignDetailLink from 'views/campaigns/components/CampaignDetailLink.component';
import CampaignsService from 'services/campaigns.service';
import dateTimeUtil from 'utils/dateTime.util';
import campaignsUtil from 'utils/campaigns.util';

const campaignTypesOptions =
  Object.entries(CAMPAIGNS.TYPES)
    .map(([value, label]) => ({ label, value }));

const statusOptions =
  Object.entries(CAMPAIGNS.STATUSES)
    .map(([value, label]) => [label, value])
    .sort()
    .map(([label, value]) => ({ label, value }));

export default function ListCampaigns(props) {
  const tableRef = useRef();
  const asyncState = useAsyncState({ isInitialized: true });
  const [campaigns, setCampaigns] = useState([]);

  useEffect(async () => await reboot(), [props.filters]);

  const reboot = async () => {
    await asyncState.allPromises(
      [getCampaigns()]
    );
  }

  const getCampaigns = async () => {
    const lineId = props.filters.line.id;
    const response = await CampaignsService.getCampaigns(lineId);
    if(response.success) {
      const campaigns = response.payload.map((campaign) => {
        const sent = campaign.statusCount?.sent ?? 0;
        const delivered = campaign.statusCount?.delivered ?? 0;
        const read = campaign.statusCount?.read ?? 0;
        return {
          ...campaign,
          type: campaignsUtil.getCampaignType(campaign),
          startedAt: campaign.startedAt ?? campaign.scheduledAt,
          sentTemplates: sent + delivered + read
        };
      });
      setCampaigns(campaigns);
      return {success: true};
    }
    return {success: false};
  }

  const elements = {
    nameFilterInput: (
      <NeoInputTextFilter ref={tableRef} field='name' placeholder='Buscar por nombre' />
    ),
    startedAtFilterRange: (
      <NeoDateRangeFilter ref={tableRef} field='startedAt' placeholder='Rango de fechas' matchFilter='custom' />
    ),
    sentTemplatesFilterInput: (
      <NeoInputTextFilter ref={tableRef} field='sentTemplates' placeholder='Buscar por cantidad' />
    ),
    statusFilterMultiSelect: (
      <NeoMultiSelectFilter
        ref={tableRef}
        options={statusOptions}
        field='status'
        placeholder='Todos'
        selectedItemsLabel='{0} estatus seleccionados'
      />
    ),
    campaignTypeFilterMultiSelect: (
      <NeoMultiSelectFilter
        ref={tableRef}
        options={campaignTypesOptions}
        field='type'
        placeholder='Todos'
        selectedItemsLabel='{0} estatus seleccionados'
      />
    ),
    nameColumnBody: (data) => (
      <CampaignDetailLink campaign={data} />
    ),
    startedAtColumnBody: (data) => (
      <>
        {
          (!data.startedAt)
            ? '-'
            : dateTimeUtil.getDateString(data.startedAt, { dateStyle: 'medium', timeStyle: 'short' })
        }
      </>
    ),
    campaignTypeBody: (data) => (
      <>{CAMPAIGNS.TYPES[data.type] ?? 'Desconocido'}</>
    ),
    statusColumnBody: (data) => (
      <NeoCellDataStatus
        status={campaignsUtil.getCampaignStatus(data.status)}
        label={campaignsUtil.getCampaignStatus(data.status)}
      />
    )
  };

  return (
    <>
      {
        (asyncState.isLoading) &&
        <NeoCard>
          <InternalSpinner />
        </NeoCard>
      }
      {
        (!asyncState.isLoading) &&
        <>
          {
            (asyncState.isSuccessfully) &&
            <NeoTable
              ref={tableRef}
              value={campaigns}
              paginator
              rows={10}
              removableSort
              sortField='startedAt'
              sortOrder={-1}
              emptyMessage='No hay campañas'
            >
              <NeoTableColumn
                field='name'
                header='Nombre'
                filter
                sortable
                filterMatchMode='contains'
                filterElement={elements.nameFilterInput}
                body={elements.nameColumnBody}

              />
              <NeoTableColumn
                header='Fecha de envío'
                field='startedAt'
                filter
                sortable
                filterMatchMode='contains'
                filterElement={elements.startedAtFilterRange}
                filterFunction={dateTimeUtil.filterDate}
                body={elements.startedAtColumnBody}
                style={{ width: '20%' }}
              />
              <NeoTableColumn
                field='type'
                header='Tipo de campaña'
                filter
                sortable
                filterMatchMode='custom'
                filterElement={elements.campaignTypeFilterMultiSelect}
                body={elements.campaignTypeBody}
                style={{ width: '20%' }}
              />
              <NeoTableColumn
                field='sentTemplates'
                header='Plantillas enviadas'
                filter
                sortable
                filterMatchMode='contains'
                filterElement={elements.sentTemplatesFilterInput}
                style={{width: '10%'}}
              />
              <NeoTableColumn
                field='status'
                header='Estatus'
                filter
                sortable
                filterMatchMode='custom'
                filterElement={elements.statusFilterMultiSelect}
                body={elements.statusColumnBody}
                style={{ width: '15%' }}
              />
            </NeoTable>
          }
          {
            (!asyncState.isSuccessfully) &&
            <NeoCard>
              <DataFetchError 
                internal 
                title='No se pudieron cargar las campañas'
                onRetry={reboot}
              />
            </NeoCard>
          }
        </>
      }
    </>
  );
}